import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSettings } from "../context/SettingsContext";
import { usePermissions } from "../context/PermissionContext";
import "./Sidebar.css";

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

/* ────────────────────────────────────────────────────────────────
   İkon seti — her menü öğesine özgün, tek çizgi (currentColor).
   Renk CSS'ten gelir; ikonların kendi rengi yoktur.
   ──────────────────────────────────────────────────────────────── */
const I = (path: React.ReactNode) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {path}
  </svg>
);

const ICONS: Record<string, React.ReactNode> = {
  // Sipariş
  "new-order": I(
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
      <path d="M14 2v6h6M12 11v6M9 14h6" />
    </>,
  ),
  orders: I(
    <>
      <rect x="4" y="3" width="16" height="18" rx="2" />
      <path d="M8 8h8M8 12h8M8 16h5" />
    </>,
  ),
  "modelist-my-orders": I(
    <>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M3.5 20c.6-3.2 2.8-5 5.5-5s4.9 1.8 5.5 5" />
      <path d="M16 10l2 2 4-4" />
    </>,
  ),
  "accounting-orders": I(
    <>
      <path d="M6 2h12v20l-3-2-3 2-3-2-3 2V2Z" />
      <path d="M9.5 8.5h5M9.5 12.5h5" />
    </>,
  ),
  "e-documents": I(
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
      <path d="M14 2v6h6" />
      <path d="M9 15l2 2 4-4" />
    </>,
  ),
  "incoming-invoices": I(
    <>
      <path d="M4 13h4l2 3h4l2-3h4" />
      <path d="M4 13V6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2Z" />
      <path d="M12 7v5M9.5 9.5 12 12l2.5-2.5" />
    </>,
  ),

  // Atölye
  workshops: I(
    <>
      <path d="M2 20V9l6 4V9l6 4V9l8 5v6H2Z" />
      <path d="M6 20v-3M11 20v-3M16 20v-3" />
    </>,
  ),
  "workshop-kanban": I(
    <>
      <rect x="3" y="3" width="5.5" height="14" rx="1.2" />
      <rect x="9.25" y="3" width="5.5" height="10" rx="1.2" />
      <rect x="15.5" y="3" width="5.5" height="17" rx="1.2" />
    </>,
  ),

  // Tanımlamalar
  firms: I(
    <>
      <rect x="3" y="7" width="18" height="13" rx="2" />
      <path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2M3 12h18" />
    </>,
  ),
  models: I(
    <>
      <path d="M9 4l3 2 3-2 5 3-2 3.5-2-1V20H7v-10.5l-2 1L3 7l6-3Z" />
    </>,
  ),
  settings: I(
    <>
      <path d="M4 6h9M17 6h3M4 12h3M11 12h9M4 18h9M17 18h3" />
      <circle cx="15" cy="6" r="2" />
      <circle cx="9" cy="12" r="2" />
      <circle cx="15" cy="18" r="2" />
    </>,
  ),
  "user-management": I(
    <>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.5 20c.7-3.3 3-5 6.5-5s5.8 1.7 6.5 5" />
      <circle cx="17.5" cy="9" r="2.5" />
      <path d="M17 14.5c2.6.3 4.2 1.7 4.7 4.5" />
    </>,
  ),
  roles: I(
    <>
      <path d="M12 22s8-3.5 8-10V5l-8-3-8 3v7c0 6.5 8 10 8 10Z" />
      <path d="M9 11.5l2 2 4-4" />
    </>,
  ),
  "audit-trail": I(
    <>
      <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h7" />
      <path d="M14 3v5h5M8 9h3M8 13h6M8 17h3" />
      <circle cx="17.5" cy="16.5" r="3" />
      <path d="m19.7 18.7 1.8 1.8" />
    </>,
  ),
  "external-tickets": I(
    <>
      <path d="M21 12a8.5 8.5 0 0 1-12.4 7.5L3 21l1.5-5.6A8.5 8.5 0 1 1 21 12Z" />
      <path d="M12 9v6M9 12h6" />
    </>,
  ),

  // Maliyet & Raporlar
  cost: I(
    <>
      <rect x="5" y="2.5" width="14" height="19" rx="2" />
      <path d="M8.5 6.5h7" />
      <path d="M8.5 11h.01M12 11h.01M15.5 11h.01M8.5 14.5h.01M12 14.5h.01M15.5 14.5h.01M8.5 18h.01M12 18h.01M15.5 18h.01" />
    </>,
  ),
  "model-costs": I(
    <>
      <path d="M20.6 13.4 13.4 20.6a2 2 0 0 1-2.8 0l-7.2-7.2V3.4h10l7.2 7.2a2 2 0 0 1 0 2.8Z" />
      <circle cx="8" cy="8" r="1.6" />
    </>,
  ),
  reports: I(
    <>
      <rect x="4" y="3" width="16" height="18" rx="2" />
      <path d="M8 16v-4M12 16V8M16 16v-6" />
    </>,
  ),
  "dynamic-reports": I(
    <>
      <path d="M3 3v18h18" />
      <path d="M7 14l4-4 3 3 5-6" />
      <path d="M19 7h.01" />
    </>,
  ),

  // Stok
  receivables: I(
    <>
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <path d="M2 10h20M6 15h4" />
    </>,
  ),
  shipping: I(
    <>
      <path d="M3 7h11v9H3zM14 10h4l3 3v3h-7z" />
      <circle cx="7" cy="18" r="1.6" />
      <circle cx="17" cy="18" r="1.6" />
    </>,
  ),
  purchasing: I(
    <>
      <path d="M3 6h18l-2 12H5L3 6Z" />
      <path d="M8 6V4a4 4 0 0 1 8 0v2M9 11v3M15 11v3" />
    </>,
  ),
  "stock-management": I(
    <>
      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
      <path d="M3.3 7 12 12l8.7-5M12 22V12" />
    </>,
  ),
  "product-tree": I(
    <>
      <rect x="9" y="2.5" width="6" height="5" rx="1.2" />
      <rect x="2.5" y="16.5" width="6" height="5" rx="1.2" />
      <rect x="15.5" y="16.5" width="6" height="5" rx="1.2" />
      <path d="M12 7.5V12M12 12H5.5v4.5M12 12h6.5v4.5" />
    </>,
  ),
  suppliers: I(
    <>
      <path d="M3 21V8l4-4h10l4 4v13" />
      <path d="M3 8h18M9 21v-6h6v6M8 12h.01M16 12h.01" />
    </>,
  ),
  cash: I(
    <>
      <rect x="2" y="6" width="20" height="13" rx="2" />
      <path d="M2 10h20M6 15h3" />
    </>,
  ),
  payables: I(
    <>
      <path d="M3 21V8l4-4h10l4 4v13" />
      <path d="M3 8h18M8 13h8M8 17h5" />
    </>,
  ),
  cheques: I(
    <>
      <rect x="2" y="5" width="20" height="14" rx="2" />
      <path d="M6 10h6M6 14h9M16 9l2 2 3-3" />
    </>,
  ),
  "trial-balance": I(
    <>
      <path d="M12 3v18M4 7h16" />
      <path d="M6 7l-3 6a3 3 0 0 0 6 0zM18 7l-3 6a3 3 0 0 0 6 0z" />
    </>,
  ),
  "finance-reports": I(
    <>
      <path d="M4 4h16v16H4z" />
      <path d="M8 15v-4M12 15V8M16 15v-6" />
    </>,
  ),
  hr: I(
    <>
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.5 20a6.5 6.5 0 0 1 13 0" />
      <path d="M16 3.5a3.5 3.5 0 0 1 0 7M21.5 20a6.5 6.5 0 0 0-5-6.3" />
    </>,
  ),
};

/* Menü verisi — id = ekran kodu (yetki sistemiyle birebir) */
const MENU_GROUPS = [
  {
    groupId: "siparis",
    groupTitle: "Sipariş",
    items: [
      { id: "new-order", title: "Yeni Sipariş", path: "/orders/new", description: "Yeni sipariş oluştur" },
      { id: "orders", title: "Siparişler", path: "/orders", description: "Sipariş yönetimi ve takibi" },
      { id: "modelist-my-orders", title: "Üzerinizdeki İşler", path: "/modelist-my-orders", description: "Size atanan siparişler" },
      { id: "accounting-orders", title: "Fatura Yönetimi", path: "/accounting-orders", description: "Fatura bilgisi ekleme" },
      { id: "e-documents", title: "Kesilen Belgeler", path: "/documents", description: "Firmalara kesilen e-belgeler" },
      { id: "incoming-invoices", title: "Gelen Faturalar", path: "/incoming-invoices", description: "Tedarikçilerden gelen e-Faturalar, kabul/red ve cariye işleme" },
      { id: "shipping", title: "Sevkiyat", path: "/shipping", description: "Sevk irsaliyesi, araç ve teslimat takibi" },
      { id: "appointments", title: "Randevu ve Takvim", path: "/appointments", description: "Dışarıdan gelen randevular ve takviminiz" },
    ],
  },
  {
    groupId: "atolye",
    groupTitle: "Atölye",
    items: [
      { id: "workshops", title: "Atölye ve Teknikler", path: "/workshops", description: "Atölye yönetimi ve ayarları" },
      { id: "workshop-kanban", title: "Atölye Kanban", path: "/workshop-kanban", description: "Atölye bazlı sipariş panosu" },
    ],
  },
  {
    groupId: "tanim",
    groupTitle: "Tanımlamalar",
    items: [
      { id: "firms", title: "Firma Yönetimi", path: "/firms", description: "Firma ekleme, düzenleme ve yönetimi" },
      { id: "models", title: "Model Yönetimi", path: "/models", description: "Model ekleme, düzenleme ve yönetimi" },
      { id: "user-management", title: "Kullanıcı Yönetimi", path: "/user-management", description: "Kullanıcıları görüntüle ve düzenle" },
      { id: "roles", title: "Rol Yönetimi", path: "/roles", description: "Roller ve yetkilendirme" },
      { id: "settings", title: "Sistem Ayarları", path: "/settings", description: "Kar marjı ve genel gider ayarları" },
      { id: "audit-trail", title: "Denetim İzi", path: "/audit-trail", description: "Kim, ne zaman, neyi değiştirdi: alan bazında değişiklik kaydı ve güvenlik olayları" },
      { id: "external-tickets", title: "Geliştirme Talepleri", path: "/tickets", description: "Geliştirme taleplerini ve önerilerini iletin" },
    ],
  },
  {
    groupId: "maliyet",
    groupTitle: "Maliyet ve Raporlar",
    items: [
      { id: "cost", title: "Maliyet Yönetimi", path: "/cost", description: "Maliyet kategorileri ve hesaplamaları" },
      { id: "model-costs", title: "Model Maliyetleri", path: "/model-costs", description: "Firma bazlı model maliyet hesaplamaları" },
      { id: "reports", title: "Mali Raporlar", path: "/reports", description: "Model maliyetleri raporu" },
      { id: "dynamic-reports", title: "Dinamik Raporlar", path: "/dynamic-reports", description: "Sipariş pivotu ve stok / finans / İK kaynaklarında rapor tasarımcısı" },
    ],
  },
  {
    groupId: "finans",
    groupTitle: "Finans",
    items: [
      { id: "receivables", title: "Cari Hesaplar", path: "/receivables", description: "Firma bakiyeleri, tahsilat ve vade takibi" },
      { id: "cash", title: "Kasa ve Banka", path: "/cash", description: "Kasa/banka hesapları, kasa defteri ve virman" },
      { id: "payables", title: "Tedarikçi Carisi", path: "/payables", description: "Tedarikçi borçları, ödeme ve vade takibi" },
      { id: "cheques", title: "Çek ve Senet", path: "/cheques", description: "Alınan/verilen çek-senet portföyü ve vade takibi" },
      { id: "trial-balance", title: "Mizan ve Nakit Akış", path: "/trial-balance", description: "Dönem mizanı, haftalık nakit akış tahmini, mali müşavir paketi" },
    ],
  },
  {
    groupId: "stok",
    groupTitle: "Stok",
    items: [
      { id: "stock-management", title: "Stok Yönetimi", path: "/stock-management", description: "Stok kategorileri, ürünler ve hareketleri" },
      { id: "product-tree", title: "Ürün Ağacı (BOM)", path: "/product-tree", description: "Mamül, yarı mamül, hammadde ilişkileri" },
      { id: "suppliers", title: "Tedarikçiler", path: "/suppliers", description: "Tedarikçi kartları ve bağlı stoklar" },
      { id: "purchasing", title: "Satın Alma", path: "/purchasing", description: "Tedarikçi siparişleri ve mal kabul" },
    ],
  },
  {
    groupId: "ik",
    groupTitle: "İnsan Kaynakları",
    items: [
      { id: "hr", title: "Personel", path: "/hr", description: "Personel kartları, departman ve pozisyonlar" },
    ],
  },
];

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { settings } = useSettings();
  const { hasScreen, has, hasModule } = usePermissions();

  // Öğe görünürlüğü — dinamik izin sistemi
  const isItemVisible = (itemId: string): boolean => {
    // Yeni Sipariş = orders ekranında "ekle" yetkisi
    if (itemId === "new-order") return has("orders:create");

    // Lisanslı modüller: sunucu lisansta olmayan modülün izinlerini zaten süzer;
    // burada bir de açıkça bakılır ki menü ile lisans hiçbir koşulda ayrışmasın.
    if (itemId === "stock-management" || itemId === "product-tree" || itemId === "suppliers")
      return hasScreen(itemId) && hasModule("stock");
    if (itemId === "e-documents" || itemId === "incoming-invoices") return hasScreen(itemId) && hasModule("invoicing");
    if (itemId === "dynamic-reports") return hasScreen(itemId) && hasModule("dynamic-reports");
    if (itemId === "workshop-kanban") return hasScreen(itemId) && hasModule("kanban");
    if (itemId === "receivables") return hasScreen(itemId) && hasModule("receivables");
    if (itemId === "cash") return hasScreen(itemId) && hasModule("finance");
    if (itemId === "payables") return hasScreen(itemId) && hasModule("finance");
    if (itemId === "cheques") return hasScreen(itemId) && hasModule("finance");
    if (itemId === "trial-balance") return hasScreen(itemId) && hasModule("finance");
    if (itemId === "purchasing") return hasScreen(itemId) && hasModule("purchasing");
    if (itemId === "shipping") return hasScreen(itemId) && hasModule("shipping");
    if (itemId === "appointments") return hasScreen(itemId) && hasModule("appointments");
    if (itemId === "hr") return hasScreen(itemId) && hasModule("hr");
    // İK varsa kullanıcılar personel kartından yönetilir (kart = kullanıcı); ayrı menü kafa karıştırır
    if (itemId === "user-management") return hasScreen(itemId) && !hasModule("hr");

    return hasScreen(itemId);
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    onClose(); // mobilde çekmeceyi kapat
  };

  const isActive = (path: string) =>
    path === "/orders"
      ? location.pathname === "/orders"
      : location.pathname === path || location.pathname.startsWith(path + "/");

  return (
    <>
      {isOpen && <div className="sidebar-overlay" onClick={onClose} />}

      <div className={`sidebar ${isOpen ? "sidebar-open" : ""}`}>
        {/* Üst: marka */}
        <div className="sidebar-header">
          <img
            src="/brand/nedaris-lockup-light.svg"
            alt="Nedaris"
            className="sidebar-logo-img"
          />
          <button className="sidebar-close" onClick={onClose} aria-label="Menüyü kapat">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Menü */}
        <nav className="sidebar-nav">
          {MENU_GROUPS.map((group) => {
            const visibleItems = group.items.filter((item) => isItemVisible(item.id));
            if (visibleItems.length === 0) return null;

            return (
              <div key={group.groupId} className="nav-group">
                <div className="nav-section-title">
                  <span>{group.groupTitle}</span>
                </div>
                {visibleItems.map((item) => (
                  <button
                    key={item.id}
                    className={`nav-item ${isActive(item.path) ? "nav-item-active" : ""}`}
                    onClick={() => handleNavigate(item.path)}
                    title={item.description}
                  >
                    <span className="nav-icon">{ICONS[item.id]}</span>
                    <span className="nav-title">{item.title}</span>
                  </button>
                ))}
              </div>
            );
          })}
        </nav>

        {/* Alt: ürün kimliği */}
        <div className="sidebar-foot">
          <span className="sidebar-foot-name">NEDARIS</span>
          <span className="sidebar-foot-sub">Üretim Yönetim Sistemi</span>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
